/**
 * HubSpot Integration Service
 *
 * TypeScript API client for the HubSpot sync endpoints on the Laravel backend.
 * Handles connection management (connect/disconnect), browsing HubSpot records,
 * importing from HubSpot, and exporting to HubSpot.
 *
 * All calls go through the standard api.ts client which handles auth token
 * injection and error handling automatically.
 */

import { api } from './api';

// -----------------------------------------------------------------
// Type Definitions
// -----------------------------------------------------------------

/** HubSpot connection status returned by GET /hubspot/settings */
export interface HubSpotConnectionStatus {
    connected: boolean;
    portal_id: string | null;
    portal_name: string | null;
    connected_at: string | null;
    connected_by: {
        id: number;
        name: string;
    } | null;
}

/** A HubSpot contact record formatted by the backend for display */
export interface HubSpotContact {
    hubspot_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    title: string;
    company: string;
    created_at: string | null;
    updated_at: string | null;
    /** True if this HubSpot contact is already linked to a local Contact or User */
    already_imported: boolean;
}

/** A HubSpot company record formatted by the backend for display */
export interface HubSpotCompany {
    hubspot_id: string;
    name: string;
    phone: string;
    domain: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    industry: string;
    created_at: string | null;
    updated_at: string | null;
    /** True if this HubSpot company is already linked to a local Company */
    already_imported: boolean;
}

/** Paginated response from browsing HubSpot contacts or companies */
export interface HubSpotListResponse<T> {
    results: T[];
    /** Cursor string for fetching the next page, null if no more pages */
    next_cursor: string | null;
    /** Total count (only available when using search, null for list) */
    total: number | null;
}

/** Result from an import operation */
export interface ImportResult {
    imported: number;
    updated: number;
    skipped: number;
    errors: Array<{
        hubspot_id?: string;
        name?: string;
        message: string;
    }>;
}

/** Result from an export operation */
export interface ExportResult {
    exported: number;
    updated: number;
    errors: Array<{
        id: number;
        type?: string;
        name: string;
        message: string;
    }>;
}

// -----------------------------------------------------------------
// Connection Management
// -----------------------------------------------------------------

/**
 * Get the current HubSpot connection status.
 * Returns whether HubSpot is connected and the portal info.
 */
export async function getSettings(): Promise<HubSpotConnectionStatus> {
    const { data } = await api.get<HubSpotConnectionStatus>('/hubspot/settings');
    return data;
}

/**
 * Save a Private App access token and test the connection.
 * If the token is valid, the backend stores the portal info and marks
 * the connection as active. Throws on invalid token.
 *
 * @param accessToken  The HubSpot Private App access token
 */
export async function connect(accessToken: string): Promise<{ message: string; portal_id: string; portal_name: string }> {
    const { data } = await api.post<{ message: string; portal_id: string; portal_name: string }>(
        '/hubspot/connect',
        { access_token: accessToken }
    );
    return data;
}

/**
 * Disconnect from HubSpot by removing the stored token and portal info.
 */
export async function disconnect(): Promise<void> {
    await api.delete('/hubspot/disconnect');
}

// -----------------------------------------------------------------
// Browse / Search HubSpot Records
// -----------------------------------------------------------------

/**
 * Browse or search HubSpot contacts. If search is provided, uses the
 * HubSpot search API; otherwise lists contacts with cursor pagination.
 *
 * @param params.search  Optional search query (name, email, phone)
 * @param params.after   Cursor for the next page
 * @param params.limit   Records per page (default 50, max 100)
 */
export async function getContacts(params?: {
    search?: string;
    after?: string;
    limit?: number;
}): Promise<HubSpotListResponse<HubSpotContact>> {
    // Build query params — only include non-empty values
    const queryParams: Record<string, string> = {};
    if (params?.search) queryParams.search = params.search;
    if (params?.after) queryParams.after = params.after;
    if (params?.limit) queryParams.limit = String(params.limit);

    const { data } = await api.get<HubSpotListResponse<HubSpotContact>>(
        '/hubspot/contacts',
        queryParams
    );
    return data;
}

/**
 * Browse or search HubSpot companies. Same pattern as getContacts().
 *
 * @param params.search  Optional search query (name, domain)
 * @param params.after   Cursor for the next page
 * @param params.limit   Records per page (default 50, max 100)
 */
export async function getCompanies(params?: {
    search?: string;
    after?: string;
    limit?: number;
}): Promise<HubSpotListResponse<HubSpotCompany>> {
    const queryParams: Record<string, string> = {};
    if (params?.search) queryParams.search = params.search;
    if (params?.after) queryParams.after = params.after;
    if (params?.limit) queryParams.limit = String(params.limit);

    const { data } = await api.get<HubSpotListResponse<HubSpotCompany>>(
        '/hubspot/companies',
        queryParams
    );
    return data;
}

// -----------------------------------------------------------------
// Import from HubSpot
// -----------------------------------------------------------------

/**
 * Import selected HubSpot contacts into the local database.
 *
 * @param hubspotIds  Array of HubSpot contact IDs to import
 * @param importAs    Whether to create local 'contact' or 'user' records
 * @param companyId   Which local company to associate the imported records with
 */
export async function importContacts(
    hubspotIds: string[],
    importAs: 'contact' | 'user',
    companyId: number | string
): Promise<ImportResult> {
    const { data } = await api.post<ImportResult>('/hubspot/import/contacts', {
        hubspot_ids: hubspotIds,
        import_as: importAs,
        company_id: companyId,
    });
    return data;
}

/**
 * Import selected HubSpot companies into the local database.
 *
 * @param hubspotIds  Array of HubSpot company IDs to import
 */
export async function importCompanies(hubspotIds: string[]): Promise<ImportResult> {
    const { data } = await api.post<ImportResult>('/hubspot/import/companies', {
        hubspot_ids: hubspotIds,
    });
    return data;
}

// -----------------------------------------------------------------
// Export to HubSpot
// -----------------------------------------------------------------

/**
 * Export selected local contacts and/or users to HubSpot.
 *
 * @param contactIds  Array of local Contact IDs to export (optional)
 * @param userIds     Array of local User IDs to export (optional)
 */
export async function exportContacts(
    contactIds?: number[],
    userIds?: number[]
): Promise<ExportResult> {
    const { data } = await api.post<ExportResult>('/hubspot/export/contacts', {
        contact_ids: contactIds ?? [],
        user_ids: userIds ?? [],
    });
    return data;
}

/**
 * Export selected local companies to HubSpot.
 *
 * @param companyIds  Array of local Company IDs (company_id column) to export
 */
export async function exportCompanies(companyIds: number[]): Promise<ExportResult> {
    const { data } = await api.post<ExportResult>('/hubspot/export/companies', {
        company_ids: companyIds,
    });
    return data;
}
